// Forms shared by the signed-in pages and the tenant-less start screen (apps/web NoTenant): an
// operator on a fresh installation has no account to open the console in yet.
import { type ModuleInfo, post, useAction, useApi } from "@keel/core";
import { CopyButton, Field, Modal } from "@keel/ui";
import { useState } from "react";

/** Two-factor enrolment: confirm the password, add the key to an authenticator, enter its code. */
export function TotpSetup({ onClose, onDone }: { onClose: () => void; onDone?: () => void }) {
  const { run, busy } = useAction();
  const [password, setPassword] = useState("");
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState("");
  if (!setup)
    return (
      <Modal
        title="Set up"
        busy={busy}
        submit="Set up"
        onClose={onClose}
        onSubmit={async () => {
          const r = await run(() =>
            post<{ secret: string; otpauthUrl: string }>("/auth/mfa/totp/setup", { password }),
          );
          if (r) setSetup(r);
        }}
      >
        <Field label="Password" hint="Confirm it's you before adding an authenticator.">
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
      </Modal>
    );
  return (
    <Modal
      title="Set up two-factor sign-in"
      busy={busy}
      submit="Turn on"
      onClose={onClose}
      onSubmit={async () => {
        if (
          (await run(() => post("/auth/mfa/totp/enable", { code }), "Two-factor sign-in is on")) !== undefined
        ) {
          onClose();
          onDone?.();
        }
      }}
    >
      <div className="stack">
        <p style={{ margin: 0 }}>
          Add Keel to your authenticator app with this key (or open the link on your phone), then enter the
          code it shows.
        </p>
        <div className="secret-box">
          <code>{setup.secret.replace(/(.{4})/g, "$1 ").trim()}</code>
          <CopyButton text={setup.secret} />
        </div>
        <a className="link sm" href={setup.otpauthUrl}>
          Open in authenticator app
        </a>
        <Field
          label="Code from the app"
          tip="The six-digit code your authenticator app shows for Keel. It changes every 30 seconds; enter the current one."
        >
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123 456"
          />
        </Field>
      </div>
    </Modal>
  );
}

interface NewTenant {
  name: string;
  slug: string;
  plan: string;
  companyName: string;
  companyCode: string;
  country: string;
  ownerEmail: string;
  ownerName: string;
  ownerPassword: string;
  modules: string[];
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/** Operator: create a customer account (tenant, first company, owner, licensed modules). */
export function NewCustomer({ onClose }: { onClose: () => void }) {
  const { run, busy } = useAction();
  const plugins = (useApi<ModuleInfo[]>("/operator/modules").data ?? []).filter((m) => !m.core);
  const [create, setCreate] = useState<NewTenant>({
    name: "",
    slug: "",
    plan: "Core",
    companyName: "",
    companyCode: "MAIN",
    country: "LV",
    ownerEmail: "",
    ownerName: "",
    ownerPassword: "",
    modules: [],
  });
  return (
    <Modal
      title="New customer"
      width={620}
      onClose={onClose}
      busy={busy}
      submit="Create account"
      onSubmit={async () => {
        const body = {
          ...create,
          companyName: create.companyName || create.name,
          ownerPassword: create.ownerPassword || null,
        };
        if (await run(() => post("/operator/tenants", body), `${create.name} created`)) onClose();
      }}
    >
      <div className="form-grid">
        <Field label="Account name">
          <input
            value={create.name}
            onChange={(e) =>
              setCreate({ ...create, name: e.target.value, slug: create.slug || slugify(e.target.value) })
            }
          />
        </Field>
        <Field
          label="Slug"
          hint="Lowercase, digits and dashes"
          tip="The account's short name in addresses, and what people type to sign in with SSO. It must be unique on the installation."
        >
          <input
            className="mono"
            value={create.slug}
            onChange={(e) => setCreate({ ...create, slug: slugify(e.target.value) })}
          />
        </Field>
        <Field
          label="Plan"
          tip="The plan name shown to the customer under Settings → Modules. Licensing modules is done in the table."
        >
          <input value={create.plan} onChange={(e) => setCreate({ ...create, plan: e.target.value })} />
        </Field>
        <Field label="Country">
          <input
            value={create.country}
            maxLength={2}
            onChange={(e) => setCreate({ ...create, country: e.target.value.toUpperCase() })}
          />
        </Field>
        <Field label="First company">
          <input
            value={create.companyName}
            placeholder={create.name}
            onChange={(e) => setCreate({ ...create, companyName: e.target.value })}
          />
        </Field>
        <Field
          label="Company code"
          tip="A short code for the first company, such as MAIN. It goes into document numbers."
        >
          <input
            className="mono"
            value={create.companyCode}
            onChange={(e) => setCreate({ ...create, companyCode: e.target.value.toUpperCase() })}
          />
        </Field>
        <Field label="Owner email">
          <input
            type="email"
            value={create.ownerEmail}
            onChange={(e) => setCreate({ ...create, ownerEmail: e.target.value })}
          />
        </Field>
        <Field label="Owner name">
          <input
            value={create.ownerName}
            onChange={(e) => setCreate({ ...create, ownerName: e.target.value })}
          />
        </Field>
        <Field label="Owner password" hint="Leave blank if the owner already has a Keel account" span>
          <input
            type="password"
            value={create.ownerPassword}
            onChange={(e) => setCreate({ ...create, ownerPassword: e.target.value })}
          />
        </Field>
        <div className="field span-2">
          <span>Modules to license</span>
          <div className="chip-set">
            {plugins.map((m) => {
              const on = create.modules.includes(m.key);
              return (
                <button
                  type="button"
                  key={m.key}
                  className={`chip ${on ? "on" : ""}`}
                  onClick={() =>
                    setCreate({
                      ...create,
                      modules: on ? create.modules.filter((k) => k !== m.key) : [...create.modules, m.key],
                    })
                  }
                >
                  {m.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}
